/**
 * Products Page - with Server-Side Pagination, Sorting and Selection
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Plus, Package, Edit, Trash2, AlertTriangle, Download, FileText, FileSpreadsheet, ChevronDown, CheckSquare } from 'lucide-react';

import ProductModal from './ProductModal';
import './styles.css';

export default function Products() {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();
  const gridRef = useRef(null);

  // Data state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});

  // Selection state
  const [selectedCount, setSelectedCount] = useState(0);

  // Quick filter state
  const [quickFilter, setQuickFilter] = useState(null);

  // Load products with server-side params
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object - always filter by type=product
      const apiFilters = { type: 'product' };
      if (filters.name) apiFilters.name = filters.name;
      if (filters.category_id && filters.category_id !== 'all') apiFilters.category_id = filters.category_id;
      if (filters.status && filters.status !== 'all') apiFilters.is_active = filters.status === 'active';

      // Apply quick filter
      if (quickFilter === 'low_stock') apiFilters.low_stock = true;
      if (quickFilter === 'inactive') apiFilters.is_active = false;
      if (quickFilter === 'out_of_stock') apiFilters.out_of_stock = true;

      const response = await api.post('/products/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: apiFilters,
      });

      if (response.data?.success) {
        setProducts(response.data.data?.items || []);
        setTotalItems(response.data.data?.totalItems || 0);
      } else {
        setProducts(response.data?.data?.items || response.data || []);
        setTotalItems(response.data?.data?.totalItems || response.data?.length || 0);
      }
    } catch (err) {
      console.error('Error loading products:', err);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters, quickFilter, t]);

  // Load categories - only product categories
  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories?type=product');
      setCategories(res.data || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }, []);

  // Load suppliers, brands, units of measure for product modal
  const loadRelatedData = useCallback(async () => {
    try {
      const [suppliersRes, brandsRes, unitsRes] = await Promise.all([
        api.get('/suppliers?active_only=true'),
        api.get('/brands?active_only=true'),
        api.get('/units-of-measure?active_only=true'),
      ]);
      setSuppliers(suppliersRes.data || []);
      setBrands(brandsRes.data || []);
      setUnitsOfMeasure(unitsRes.data || []);
    } catch (err) {
      console.error('Error loading related data:', err);
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadRelatedData();
  }, [loadCategories, loadRelatedData]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Handle page change from grid
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPageNumber(newPage);
    setPageSize(newPageSize);
  }, []);

  // Handle sort change from grid
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    if (newSortBy && newSortDirection) {
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
    }
  }, []);

  // Handle filter search
  const handleSearch = useCallback(() => {
    setPageNumber(1); // Reset to first page on search
    loadProducts();
  }, [loadProducts]);

  // Quick filter handler
  const handleQuickFilter = useCallback((filter) => {
    setQuickFilter(prev => prev === filter ? null : filter);
    setPageNumber(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setQuickFilter(null);
    setPageNumber(1);
  }, []);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingProduct?.id) {
        await api.put(`/products/${editingProduct.id}`, data);
      } else {
        await api.post('/products', data);
      }
      toast.success(t('products.saveSuccess'));
      setModalOpen(false);
      setEditingProduct(null);
      await loadProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingProduct to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      // Small delay to allow modal close animation
      setTimeout(() => setEditingProduct(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success(t('products.deleteSuccess'));
      setDeleteId(null);
      await loadProducts();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  // Export handlers
  const handleExportCSV = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    gridRef.current?.exportCSV(onlySelected);
  }, [selectedCount]);

  const handleExportJSON = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    const data = gridRef.current?.exportJSON(onlySelected);
    if (!data?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `produtos_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCount]);

  // Track selection changes
  const handleSelectionChanged = useCallback(() => {
    const count = gridRef.current?.getSelectedCount() || 0;
    setSelectedCount(count);
  }, []);

  const filterDefinitions = useMemo(() => [
    {
      key: 'name',
      label: t('products.name'),
      type: 'text',
      placeholder: 'Buscar produto...',
    },
    {
      key: 'category_id',
      label: t('products.category'),
      type: 'select',
      placeholder: 'Todas',
      options: [
        { value: 'all', label: 'Todas' },
        ...categories.map(c => ({ value: c.id, label: c.name })),
      ],
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
      ],
    },
  ], [t, categories]);

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: t('products.name'),
      flex: 2,
      sortable: true,
      cellRenderer: (params) => {
        const lowStock = params.data.track_stock && params.data.stock <= (params.data.min_stock || 0);
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{params.value}</span>
            {lowStock && <AlertTriangle className="h-4 w-4 text-orange-500" />}
          </div>
        );
      },
    },
    {
      field: 'category_id',
      headerName: t('products.category'),
      flex: 1,
      sortable: true,
      valueFormatter: (params) => {
        const cat = categories.find(c => c.id === params.value);
        return cat?.name || '-';
      },
    },
    {
      field: 'price',
      headerName: t('products.price'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        <span className="font-mono font-medium">{formatCurrency(params.value)}</span>
      ),
    },
    {
      field: 'stock',
      headerName: t('products.stock'),
      flex: 0.8,
      sortable: true,
      cellRenderer: (params) => {
        if (!params.data.track_stock) return <span className="text-muted-foreground">-</span>;
        const lowStock = params.value <= (params.data.min_stock || 0);
        return (
          <span className={lowStock ? 'text-orange-500 font-medium' : ''}>
            {params.value}
          </span>
        );
      },
    },
    {
      field: 'is_active',
      headerName: t('common.status'),
      flex: 0.8,
      sortable: true,
      cellRenderer: (params) => (
        <span className={params.value ? 'datagrid-status-active' : 'datagrid-status-inactive'}>
          {params.value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      flex: 0.8,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="datagrid-actions">
          <Button variant="ghost" size="icon" onClick={() => { setEditingProduct(params.data); setModalOpen(true); }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(params.data.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ], [t, categories, formatCurrency]);

  // Custom export actions with selection option
  const ExportActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedCount}</Badge>
          )}
          <ChevronDown className="h-3 w-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportCSV(false)}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar Tudo (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportJSON(false)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar Tudo (JSON)
        </DropdownMenuItem>
        {selectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportCSV(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportJSON(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (JSON)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="products-page" data-testid="products-page">
      <PageHeader
        title={t('products.title')}
        icon={Package}
        subtitle={`${totalItems} produtos cadastrados`}
        actions={
          <div className="flex items-center gap-2">
            <ExportActions />
            <Button onClick={() => { setEditingProduct(null); setModalOpen(true); }} data-testid="new-product-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('products.newProduct')}
            </Button>
          </div>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        showExport={false}
      />

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          variant={quickFilter === 'low_stock' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('low_stock')}
          className={quickFilter === 'low_stock' ? '' : 'hover:bg-amber-500/10'}
          data-testid="quick-filter-low-stock"
        >
          <AlertTriangle className="h-4 w-4 mr-1" />
          Estoque Baixo
        </Button>
        <Button
          variant={quickFilter === 'out_of_stock' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('out_of_stock')}
          className={quickFilter === 'out_of_stock' ? '' : 'hover:bg-red-500/10'}
          data-testid="quick-filter-out-of-stock"
        >
          <Package className="h-4 w-4 mr-1" />
          Sem Estoque
        </Button>
        <Button
          variant={quickFilter === 'inactive' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('inactive')}
          className={quickFilter === 'inactive' ? '' : 'hover:bg-gray-500/10'}
          data-testid="quick-filter-inactive"
        >
          <Package className="h-4 w-4 mr-1" />
          Inativos
        </Button>
        {quickFilter && (
          <Button variant="ghost" size="sm" onClick={() => setQuickFilter(null)}>
            Limpar filtro
          </Button>
        )}
      </div>

      <DataGrid
        ref={gridRef}
        title="Lista de Produtos"
        columnDefs={columnDefs}
        rowData={products}
        loading={loading}
        enableSelection={true}
        getRowId={(params) => params.data.id}
        serverSide={true}
        totalItems={totalItems}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        paginationPageSize={pageSize}
        onSelectionChanged={handleSelectionChanged}
      />

      <ProductModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        product={editingProduct}
        categories={categories}
        suppliers={suppliers}
        brands={brands}
        unitsOfMeasure={unitsOfMeasure}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('products.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
